import { Helmet } from 'react-helmet-async';
import './landing-variants.css';

/* V10 — "Swiss Archive"
   International Typographic Style. Rigid grid, heavy grotesque type,
   high-contrast black/white with single bold accent: International Orange (#FF4F00).
   Schibsted Grotesk (display) + Libre Franklin (body). */

const orange = '#FF4F00';
const black = '#0a0a0a';
const white = '#fafafa';
const grey = '#6b6b6b';

const features = [
  { num: '01', title: 'Agenda intelligent', desc: 'Optimisation automatique des créneaux, zéro trou dans votre planning.' },
  { num: '02', title: 'Dossiers patients', desc: 'Historique clinique complet, accessible en un geste depuis le fauteuil.' },
  { num: '03', title: 'Facturation fluide', desc: 'Devis, factures et relances — tout se gère sans quitter votre flux.' },
  { num: '04', title: 'Multi-cabinet', desc: 'Pilotez plusieurs sites depuis un tableau de bord unifié, en temps réel.' },
];

const stats = [
  { value: '240+', label: 'cabinets' },
  { value: '61 000', label: 'rdv/mois' },
  { value: '34 %', label: 'temps économisé' },
];

export default function LandingV10() {
  const display = "'Schibsted Grotesk', sans-serif";
  const body = "'Libre Franklin', sans-serif";

  return (
    <div className="doctif-page" style={{ fontFamily: body, background: white, color: black }}>
      <Helmet><title>doctif — Swiss Archive</title></Helmet>

      {/* ── Top rule ── */}
      <div style={{ height: 4, background: orange }} />

      {/* ── Header ── */}
      <header className="doctif-shell" style={{ padding: '32px 0 24px', position: 'relative', zIndex: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div style={{ fontFamily: display, fontWeight: 900, fontSize: 32, letterSpacing: '-0.04em', textTransform: 'uppercase' }}>
            doctif<span style={{ color: orange }}>.</span>
          </div>
          <nav className="doctif-chip-row" style={{ alignItems: 'center', gap: 6 }}>
            {Array.from({ length: 15 }, (_, i) => (
              <a key={i} className="doctif-chip" href={`/${i + 1}/`} style={{
                borderRadius: 0, padding: '4px 10px', fontSize: 11, fontFamily: display, fontWeight: 700,
                background: i === 9 ? orange : 'transparent', color: i === 9 ? white : grey,
                border: `1px solid ${i === 9 ? orange : '#ccc'}`,
              }}>{String(i + 1).padStart(2, '0')}</a>
            ))}
            <a href="/accounts/login/" style={{
              fontFamily: display, fontWeight: 700, fontSize: 12, textTransform: 'uppercase',
              letterSpacing: '0.08em', color: black, textDecoration: 'none',
              padding: '6px 16px', border: `2px solid ${black}`, marginLeft: 10,
            }}>Connexion</a>
          </nav>
        </div>
        {/* Thick rule under header */}
        <div style={{ height: 2, background: black, marginTop: 20 }} />
      </header>

      {/* ── Hero — Swiss Grid ── */}
      <section className="doctif-shell" style={{ paddingTop: 40, paddingBottom: 60 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, borderTop: `1px solid #ddd` }}>
          {/* Left column */}
          <div style={{ borderRight: '1px solid #ddd', paddingRight: 60, paddingTop: 40 }}>
            <p className="doctif-fade-up" style={{
              fontFamily: display, fontWeight: 700, fontSize: 11, letterSpacing: '0.25em',
              textTransform: 'uppercase', color: grey, marginBottom: 24,
            }}>
              Suite Clinique v.10
            </p>
            <h1 className="doctif-fade-up doctif-delay-1" style={{
              fontFamily: display, fontWeight: 900, fontSize: 'clamp(2.6rem, 5.5vw, 5rem)',
              lineHeight: 0.92, letterSpacing: '-0.04em', textTransform: 'uppercase', marginBottom: 32,
            }}>
              Rigueur<br />
              typographique<span style={{ color: orange }}>.</span><br />
              Clarté<br />
              absolue<span style={{ color: orange }}>.</span>
            </h1>
            <div className="doctif-fade-up doctif-delay-2" style={{ display: 'flex', gap: 12 }}>
              <a href="/accounts/login/" style={{
                display: 'inline-block', padding: '14px 32px', background: orange, color: white,
                fontFamily: display, fontWeight: 700, fontSize: 13, letterSpacing: '0.1em',
                textTransform: 'uppercase', textDecoration: 'none', transition: 'background 0.2s',
              }}>Commencer</a>
              <a href="#modules" style={{
                display: 'inline-block', padding: '14px 32px', border: `2px solid ${black}`,
                fontFamily: display, fontWeight: 700, fontSize: 13, letterSpacing: '0.1em',
                textTransform: 'uppercase', textDecoration: 'none', color: black, transition: 'all 0.2s',
              }}>Explorer</a>
            </div>
          </div>
          {/* Right column — description + stats */}
          <div style={{ paddingLeft: 60, paddingTop: 40 }}>
            <p className="doctif-fade-up doctif-delay-2" style={{
              fontSize: 17, lineHeight: 1.75, color: '#444', maxWidth: 440, marginBottom: 48,
            }}>
              doctif fusionne agenda, dossiers patients et facturation dans un système unique.
              Pas de décoration superflue — chaque pixel est au service de la fonction.
            </p>
            {/* Stats grid */}
            <div className="doctif-fade-up doctif-delay-3" style={{
              display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0, borderTop: `2px solid ${black}`,
            }}>
              {stats.map((s, i) => (
                <div key={i} style={{
                  borderRight: i < 2 ? `1px solid #ddd` : 'none',
                  paddingTop: 20, paddingRight: i < 2 ? 20 : 0, paddingLeft: i > 0 ? 20 : 0,
                }}>
                  <div style={{ fontFamily: display, fontWeight: 900, fontSize: 36, letterSpacing: '-0.03em' }}>{s.value}</div>
                  <div style={{ fontSize: 12, color: grey, letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: 4 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Orange divider band ── */}
      <div style={{ background: orange, padding: '16px 0' }}>
        <div className="doctif-shell" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: display, fontWeight: 700, fontSize: 13, letterSpacing: '0.15em', textTransform: 'uppercase', color: white }}>
            Système modulaire
          </span>
          <span style={{ fontFamily: display, fontWeight: 400, fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>004 Modules</span>
        </div>
      </div>

      {/* ── Features Grid — 2×2 strict Swiss ── */}
      <section id="modules" className="doctif-shell" style={{ paddingTop: 60, paddingBottom: 60 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
          {features.map((f, i) => (
            <div key={i} className="doctif-fade-up" style={{
              borderTop: `1px solid #ddd`, borderRight: i % 2 === 0 ? '1px solid #ddd' : 'none',
              padding: '36px 32px',
              animationDelay: `${i * 120}ms`,
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 16 }}>
                <span style={{ fontFamily: display, fontWeight: 900, fontSize: 48, color: orange, lineHeight: 1, letterSpacing: '-0.04em' }}>{f.num}</span>
                <span style={{ fontFamily: display, fontWeight: 700, fontSize: 20, letterSpacing: '-0.02em', textTransform: 'uppercase' }}>{f.title}</span>
              </div>
              <p style={{ fontSize: 15, lineHeight: 1.7, color: '#555', maxWidth: 360 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Dashboard Preview — Monochrome Grid ── */}
      <section style={{ background: black, padding: '60px 0' }}>
        <div className="doctif-shell">
          <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 40 }}>
            <div>
              <p style={{ fontFamily: display, fontWeight: 700, fontSize: 11, letterSpacing: '0.25em', textTransform: 'uppercase', color: grey, marginBottom: 12 }}>
                Interface
              </p>
              <h2 style={{ fontFamily: display, fontWeight: 900, fontSize: 28, color: white, lineHeight: 1.1, letterSpacing: '-0.03em', textTransform: 'uppercase' }}>
                Tableau de bord
              </h2>
            </div>
            <div style={{ background: '#161616', border: '1px solid #333', padding: 32 }}>
              {/* Mock dashboard grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
                {['Aujourd\'hui', 'Semaine', 'Mois', 'Année'].map((label, i) => (
                  <div key={i} style={{ background: i === 0 ? orange : '#222', padding: '16px 12px', textAlign: 'center' }}>
                    <div style={{ fontFamily: display, fontWeight: 900, fontSize: 28, color: i === 0 ? white : '#666', letterSpacing: '-0.03em' }}>
                      {['12', '67', '284', '3.4k'][i]}
                    </div>
                    <div style={{ fontSize: 10, color: i === 0 ? 'rgba(255,255,255,0.7)' : '#555', letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: 4 }}>{label}</div>
                  </div>
                ))}
              </div>
              {/* Rows */}
              {['Dr. Martin — 09:00 — Contrôle', 'Dr. Leroy — 09:30 — Détartrage', 'Dr. Martin — 10:15 — Couronne'].map((row, i) => (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '12px 0', borderTop: '1px solid #2a2a2a', fontSize: 13, color: '#888',
                  fontFamily: body,
                }}>
                  <span>{row}</span>
                  <span style={{ fontFamily: display, fontWeight: 700, fontSize: 11, color: orange, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                    {['CONFIRMÉ', 'EN ATTENTE', 'CONFIRMÉ'][i]}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="doctif-shell" style={{ paddingTop: 60, paddingBottom: 60 }}>
        <p style={{ fontFamily: display, fontWeight: 700, fontSize: 11, letterSpacing: '0.25em', textTransform: 'uppercase', color: grey, marginBottom: 8 }}>
          Témoignages
        </p>
        <div style={{ height: 2, background: black, marginBottom: 40, maxWidth: 60 }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0 }}>
          {[
            { q: 'La rigueur de cette interface nous a convaincus dès le premier jour.', a: 'Dr. Elise Moreau', r: 'Orthodontiste, Paris' },
            { q: 'Pas un pixel de trop. Exactement ce dont un cabinet a besoin.', a: 'Dr. Julien Fabre', r: 'Dentiste, Lyon' },
            { q: 'L\'architecture du système reflète la précision de notre métier.', a: 'Dr. Nadia Hamdi', r: 'Parodontiste, Marseille' },
          ].map((t, i) => (
            <div key={i} style={{
              borderRight: i < 2 ? '1px solid #ddd' : 'none',
              paddingRight: i < 2 ? 32 : 0, paddingLeft: i > 0 ? 32 : 0,
            }}>
              <p style={{ fontSize: 16, lineHeight: 1.7, color: '#333', marginBottom: 20 }}>« {t.q} »</p>
              <p style={{ fontFamily: display, fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t.a}</p>
              <p style={{ fontSize: 12, color: grey }}>{t.r}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Trust Bar — Clean rule boxes ── */}
      <div style={{ borderTop: '1px solid #ddd', borderBottom: '1px solid #ddd' }}>
        <div className="doctif-shell" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0 }}>
          {[
            { k: 'CHIFFREMENT', v: 'AES-256' },
            { k: 'HÉBERGEMENT', v: 'HDS France' },
            { k: 'DISPONIBILITÉ', v: '99.9 %' },
            { k: 'SUPPORT', v: '< 2 h' },
          ].map((t, i) => (
            <div key={i} style={{
              padding: '20px 0', borderRight: i < 3 ? '1px solid #ddd' : 'none',
              paddingLeft: i > 0 ? 24 : 0, paddingRight: i < 3 ? 24 : 0,
            }}>
              <div style={{ fontFamily: display, fontWeight: 700, fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: grey, marginBottom: 4 }}>{t.k}</div>
              <div style={{ fontFamily: display, fontWeight: 900, fontSize: 18, letterSpacing: '-0.02em' }}>{t.v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── CTA ── */}
      <section className="doctif-shell" style={{ paddingTop: 80, paddingBottom: 80, textAlign: 'center' }}>
        <h2 style={{
          fontFamily: display, fontWeight: 900, fontSize: 'clamp(2rem, 4vw, 3.6rem)',
          letterSpacing: '-0.04em', textTransform: 'uppercase', lineHeight: 1, marginBottom: 24,
        }}>
          Prêt pour<br />la précision<span style={{ color: orange }}>?</span>
        </h2>
        <a href="/accounts/login/" style={{
          display: 'inline-block', padding: '16px 48px', background: orange, color: white,
          fontFamily: display, fontWeight: 700, fontSize: 14, letterSpacing: '0.12em',
          textTransform: 'uppercase', textDecoration: 'none',
        }}>Démarrer maintenant</a>
      </section>

      {/* ── Footer ── */}
      <footer style={{ borderTop: `2px solid ${black}`, padding: '20px 0' }}>
        <div className="doctif-shell" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: display, fontWeight: 700, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            doctif<span style={{ color: orange }}>.</span> — Direction Swiss Archive
          </span>
          <span style={{ fontSize: 12, color: grey }}>Version 10 — {new Date().getFullYear()}</span>
        </div>
      </footer>

      {/* Bottom orange rule */}
      <div style={{ height: 4, background: orange }} />
    </div>
  );
}
